import { useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useBuildingContext } from "../lib/buildingContext";
import { useClientContext } from "../lib/clientContext";
import { IconBuilding, IconCheck, IconChevronDown, IconSearch } from "./icons";
import { buildingPath, sectionOfPath } from "./navItems";

/** Pagina's die zelf de gekozen gebouw volgen: daar blijf je staan als je wisselt. */
const FOLLOWS = ["/taken", "/mjop", "/conditie", "/meterstanden"];

/**
 * Gebouwkiezer in de bovenbalk: hier kies je in welk gebouw je werkt. Wisselen houdt je op hetzelfde
 * onderdeel (Assets van het ene gebouw wordt Assets van het andere). "Alle gebouwen" geeft de overzichten.
 */
export function BuildingSwitcher() {
  const { id, building, buildings: all, setBuilding } = useBuildingContext();
  // Met een gekozen klant alleen de gebouwen van die klant.
  const { id: clientId } = useClientContext();
  const buildings = clientId ? all?.filter((b) => b.clientId === clientId) : all;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const popId = useId();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    setQ("");
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      btn.current?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function choose(next: string | null) {
    setOpen(false);
    btn.current?.focus();
    const sec = sectionOfPath(pathname);
    const stays = FOLLOWS.includes(pathname);
    setBuilding(next);
    if (next) {
      if (sec) navigate(buildingPath(next, sec));
      else if (pathname === "/keuringen") navigate(buildingPath(next, "keuringen"));
      else if (!stays) navigate(buildingPath(next, "overzicht"));
    } else if (sec === "keuringen") navigate("/keuringen");
    else if (sec === "meters") navigate("/meterstanden");
    else if (!stays) navigate("/");
  }

  const t = q.trim().toLowerCase();
  const list = (buildings ?? []).filter(
    (b) => !t || [b.code, b.name, b.city].filter(Boolean).join(" ").toLowerCase().includes(t),
  );
  const current = building ? `${building.code} · ${building.name}` : "Alle gebouwen";

  return (
    <div className="bswitch" ref={ref}>
      <button
        ref={btn}
        type="button"
        className="bswitch-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        aria-label={`Gebouw: ${current}. Ander gebouw kiezen`}
        onClick={() => setOpen(!open)}
      >
        <IconBuilding size={20} aria-hidden="true" />
        {building ? (
          <span className="bswitch-label">
            <span className="code">{building.code}</span> <span className="bswitch-name">{building.name}</span>
          </span>
        ) : (
          <span className="bswitch-label">{id ? "Gebouw" : "Alle gebouwen"}</span>
        )}
        <IconChevronDown size={18} aria-hidden="true" />
      </button>
      {open && (
        <div className="popover bswitch-pop" id={popId} role="dialog" aria-label="Gebouw kiezen">
          {(buildings?.length ?? 0) > 6 && (
            <label className="bswitch-search">
              <IconSearch size={18} aria-hidden="true" />
              <input
                type="search"
                autoFocus
                placeholder="Zoek op code, naam of plaats"
                aria-label="Gebouw zoeken"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
          )}
          <div className="bswitch-list">
            <button type="button" className="menuitem" aria-current={!id} onClick={() => choose(null)}>
              {!id ? <IconCheck size={20} aria-hidden="true" /> : <span className="ico-space" aria-hidden="true" />}
              <strong className="grow">Alle gebouwen</strong>
            </button>
            {list.map((b) => (
              <button
                key={b.id}
                type="button"
                className="menuitem"
                aria-current={b.id === id}
                onClick={() => choose(b.id)}
              >
                {b.id === id ? (
                  <IconCheck size={20} aria-hidden="true" />
                ) : (
                  <span className="ico-space" aria-hidden="true" />
                )}
                <span className="grow">
                  <span className="code small">{b.code}</span> {b.name}
                  {b.city && <span className="muted small block">{b.city}</span>}
                </span>
              </button>
            ))}
            {t && !list.length && <p className="muted small bswitch-none">Geen gebouw gevonden.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
